import { Disclosure, Transition } from '@headlessui/react';
import dayjs from 'dayjs';
import capitalize from 'lodash/capitalize';
import omit from 'lodash/omit';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { Filters } from '@/modules/common/pages/Certificates.tsx';
import { CalendarInput, CalendarInputProps, Select, Spinner } from 'ui';
import { getDisabledDate } from 'ui/utils';
import { ShareCertificateStatus } from 'api';

const statusOptions = Object.values(ShareCertificateStatus).map(status => ({
  label: capitalize(status),
  value: status,
}));

interface CertificatesFiltersProps {
  filters: Filters;
  setFilters: Dispatch<SetStateAction<Filters>>;
  isFetching: boolean;
}

const CertificatesFilters = ({ filters, setFilters, isFetching }: CertificatesFiltersProps) => {
  const handleChangeStatus = useCallback(
    (status: ShareCertificateStatus | null) => {
      setFilters(filters => (status ? { ...filters, status } : omit(filters, 'status')));
    },
    [setFilters],
  );

  const handleChangeDate: CalendarInputProps['rangeOnChange'] = dates => {
    if (dates) {
      const start_date = dayjs(dates[0]).format('YYYY-MM-DD');
      const end_date = dayjs(dates[1]).format('YYYY-MM-DD');
      setFilters(filters =>
        start_date && end_date
          ? { ...filters, from_date: start_date, to_date: end_date }
          : omit(filters, ['from_date', 'to_date']),
      );
      return;
    }
    setFilters(filters => omit(filters, ['from_date', 'to_date']));
  };

  return (
    <Transition
      enter="transition duration-100 ease-out"
      enterFrom="transform scale-95 opacity-0"
      enterTo="transform scale-100 opacity-100"
      leave="transition duration-75 ease-out"
      leaveFrom="transform scale-100 opacity-100"
      leaveTo="transform scale-95 opacity-0"
    >
      <Disclosure.Panel static>
        <div className="flex justify-end">
          <div className="flex flex-wrap gap-2 items-end justify-end max-w-[850px] [&>*]:max-w-[400px] [&>*]:flex-1">
            {/*<Select placeholder="Register" />*/}
            {/*<Select placeholder="Class" />*/}
            <Select
              isClearable
              label=""
              value={filters.status}
              placeholder="Status"
              className="min-w-[150px]"
              options={statusOptions}
              onChange={handleChangeStatus}
            />
            <CalendarInput
              range
              rangeOnChange={handleChangeDate}
              disabledDate={getDisabledDate}
              className="w-auto min-w-[250px] [&>div]:pb-3.5"
            />
            {isFetching && <Spinner size="md" variant="primary" />}
          </div>
        </div>
      </Disclosure.Panel>
    </Transition>
  );
};

export default CertificatesFilters;
